import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Radar as RadarIcon, AlertTriangle, Zap, Sparkles, Loader2, Wand2, Trash2, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TrendScout } from "@/components/TrendScout";
import { HeatBadge, PriorityBadge, FlagBadges } from "@/components/Badges";
import { TYPE_LABEL } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { DocScanButton } from "@/components/DocScanButton";

const HEAT_FILTERS = [
  { key: "all", label: "Alle" },
  { key: "hot", label: "Heiß" },
  { key: "warm", label: "Warm" },
  { key: "cold", label: "Kalt" },
];

export default function Themenradar() {
  const { accountKey, search } = useOutletContext();
  const [topics, setTopics] = useState([]);
  const [heat, setHeat] = useState("all");
  const [tab, setTab] = useState("themen");
  const [open, setOpen] = useState(false);
  const [ideasFor, setIdeasFor] = useState(null);
  const [connected, setConnected] = useState(false);
  const [deriving, setDeriving] = useState(false);

  const load = () => api.get("/topics", { params: { account: accountKey } }).then(({ data }) => setTopics(data));

  const deriveFromInstagram = async () => {
    setDeriving(true);
    try {
      const { data } = await api.post("/ai/rebuild-board", { account_key: accountKey, replace: true });
      const jobId = data.job_id;
      const poll = async () => {
        const { data: job } = await api.get(`/ai/rebuild-board/jobs/${jobId}`);
        if (job.status === "done") {
          const r = job.result || {};
          await load();
          toast.success(
            `Board live: ${r.topics?.length || 0} Themen · ${r.drafts_created || 0} Entwürfe · ${r.events_created || 0} Events · ${r.partners_created || 0} Partner · ${r.learnings_created || 0} Learnings`,
            { duration: 6000 },
          );
          setDeriving(false);
        } else if (job.status === "error") {
          toast.error(job.error || "Board-Aufbau fehlgeschlagen");
          setDeriving(false);
        } else {
          setTimeout(poll, 3000);
        }
      };
      setTimeout(poll, 3000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Board-Aufbau fehlgeschlagen");
      setDeriving(false);
    }
  };

  const deleteTopic = async (id) => {
    try {
      await api.delete(`/topics/${id}`);
      setTopics((ts) => ts.filter((t) => t.id !== id));
      toast.success("Thema gelöscht");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  useEffect(() => {
    if (accountKey) {
      load();
      api.get("/meta/status", { params: { account: accountKey } })
        .then(({ data }) => setConnected(!!data.connected))
        .catch(() => setConnected(false));
    }
  }, [accountKey]);

  const filtered = useMemo(() => {
    const q = (search || "").toLowerCase();
    return topics.filter(
      (t) =>
        (heat === "all" || t.heat === heat) &&
        (!q || t.title.toLowerCase().includes(q) || t.summary.toLowerCase().includes(q)),
    );
  }, [topics, heat, search]);

  const flagsOf = (t) => {
    const f = [];
    if (t.heat === "hot") f.push("hot");
    if (t.event_relevant) f.push("event");
    if (t.reach_lever === "high") f.push("high_reach");
    if (t.sensitive) f.push("sensitive");
    return f;
  };

  return (
    <div className="mx-auto min-w-0 max-w-[1400px] overflow-x-clip p-4 sm:p-6 md:p-8 animate-rise">
      <div className="flex min-w-0 flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-neutral-400 flex items-center gap-1.5">
            <RadarIcon className="h-3.5 w-3.5" /> Themenradar
          </p>
          <h1 className="mt-1 break-words font-head text-2xl font-bold tracking-tight text-neutral-900 sm:text-3xl">Aktuelle Themen & Prioritäten</h1>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-2">
          {connected && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button
                  data-testid="derive-topics-btn"
                  disabled={deriving}
                  className="inline-flex max-w-full items-center justify-center gap-2 bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:opacity-90 transition disabled:opacity-60"
                >
                  {deriving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Board aus Instagram aufbauen
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Board komplett aus echten Instagram-Daten aufbauen?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Die KI analysiert die echten Beiträge dieses Kontos und befüllt das gesamte Board mit realen Inhalten:
                    Themen, fertige Reel-Entwürfe (in der Pipeline), Events, Partner und Learnings.
                    <strong> Alle bisherigen Demo-/Platzhalter-Daten dieses Accounts werden ersetzt.</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction data-testid="derive-topics-confirm" onClick={deriveFromInstagram} className="bg-indigo-600 hover:bg-indigo-700">
                    Board live aufbauen
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          <CreateTopicDialog open={open} setOpen={setOpen} accountKey={accountKey} onCreated={load} />
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-6 flex max-w-full flex-wrap items-center gap-1.5 border-b border-neutral-200 pb-px">
        <button
          data-testid="radar-tab-themen"
          onClick={() => setTab("themen")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            tab === "themen" ? "border-fuchsia-500 text-neutral-900 bg-fuchsia-50/60" : "border-transparent text-neutral-500 hover:text-neutral-800"
          }`}
        >
          <RadarIcon className="h-4 w-4" /> Themen
        </button>
        <button
          data-testid="radar-tab-trends"
          onClick={() => setTab("trends")}
          className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
            tab === "trends" ? "border-fuchsia-500 text-neutral-900 bg-fuchsia-50/60" : "border-transparent text-neutral-500 hover:text-neutral-800"
          }`}
        >
          <TrendingUp className="h-4 w-4" /> Trend-Scout
        </button>
      </div>

      {tab === "trends" && <TrendScout accountKey={accountKey} />}

      {tab === "themen" && (<>
      {/* Filter bar */}
      <div className="flex items-center gap-2 mt-6 flex-wrap">
        {HEAT_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`heat-filter-${f.key}`}
            onClick={() => setHeat(f.key)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
              heat === f.key ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"
            }`}
          >
            {f.label}
          </button>
        ))}
        <span className="text-sm text-neutral-400 ml-2">{filtered.length} Themen</span>
      </div>

      {/* List */}
      <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((t) => (
          <div
            key={t.id}
            data-testid={`topic-card-${t.id}`}
            className={`bg-white border rounded-xl p-5 hover:shadow-md transition-all ${
              t.sensitive ? "border-rose-200" : "border-neutral-200"
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-head font-semibold text-neutral-900 text-[17px] leading-snug">{t.title}</h3>
              <div className="flex items-center gap-2 shrink-0">
                <HeatBadge heat={t.heat} />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      data-testid={`topic-delete-btn-${t.id}`}
                      title="Thema löschen"
                      className="text-neutral-300 hover:text-rose-500 transition p-1 -mr-1"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Thema löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        „{t.title}" wird dauerhaft aus dem Themenradar entfernt. Das kann nicht rückgängig gemacht werden.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction
                        data-testid={`topic-delete-confirm-${t.id}`}
                        onClick={() => deleteTopic(t.id)}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        Löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <p className="text-sm text-neutral-500 mt-2 leading-relaxed">{t.summary}</p>
            <div className="mt-3"><FlagBadges flags={flagsOf(t)} /></div>
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-100">
              <div className="flex items-center gap-2">
                <PriorityBadge priority={t.priority} />
                {typeof t.virality_score === "number" && (
                  <span
                    data-testid={`topic-virality-${t.id}`}
                    title="KI-Prognose virales Potenzial"
                    className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                      t.virality_score >= 70 ? "bg-fuchsia-100 text-fuchsia-700"
                      : t.virality_score >= 40 ? "bg-amber-100 text-amber-700"
                      : "bg-neutral-100 text-neutral-500"
                    }`}
                  >
                    <Zap className="h-3 w-3" /> {t.virality_score}
                  </span>
                )}
              </div>
              <span className="text-xs text-neutral-400 flex items-center gap-1">
                {t.reach_lever === "high" && <Zap className="h-3.5 w-3.5 text-blue-500" />}
                {t.sensitive && <AlertTriangle className="h-3.5 w-3.5 text-rose-500" />}
                Quelle: {t.source || "—"}
              </span>
            </div>
            <button
              data-testid={`topic-ideas-btn-${t.id}`}
              onClick={() => setIdeasFor(t)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:opacity-90 transition"
            >
              <Sparkles className="h-4 w-4" /> KI-Ideen generieren
            </button>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-neutral-400 py-16">Keine Themen gefunden.</div>
        )}
      </div>
      </>)}

      <IdeasDialog topic={ideasFor} accountKey={accountKey} onClose={() => setIdeasFor(null)} />
    </div>
  );
}

function IdeasDialog({ topic, accountKey, onClose }) {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState("");

  useEffect(() => {
    if (!topic) return;
    setIdeas([]);
    setLoading(true);
    api
      .post("/ai/ideas", { account_key: accountKey, title: topic.title, summary: topic.summary })
      .then(({ data }) => {
        setIdeas(data.ideas || []);
        if (data.data_backed) toast.success("Ideen auf Basis echter Performance-Daten");
      })
      .catch((e) => toast.error(e.response?.data?.detail || "KI-Ideen fehlgeschlagen"))
      .finally(() => setLoading(false));
  }, [topic, accountKey]);

  const createContent = async (idea) => {
    setCreating(idea.title);
    try {
      const type = ["reel", "story", "post"].includes(idea.format) ? idea.format : "reel";
      await api.post("/content", {
        account_key: accountKey, title: idea.title, content_type: type,
        status: "idea", hook: idea.hook, caption: idea.angle || "",
        flags: topic.heat === "hot" ? ["hot"] : [],
      });
      toast.success("Als Content-Idee in der Pipeline angelegt");
    } catch {
      toast.error("Anlegen fehlgeschlagen");
    } finally {
      setCreating("");
    }
  };

  return (
    <Dialog open={!!topic} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ideas-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" /> KI-Content-Ideen
          </DialogTitle>
          <DialogDescription>Vorschläge zu „{topic?.title}". Direkt als Content-Idee übernehmen.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-neutral-400">
            <Loader2 className="h-7 w-7 animate-spin text-blue-500" />
            <p className="text-sm mt-3">Die KI denkt nach…</p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[55vh] overflow-y-auto">
            {ideas.map((idea, i) => (
              <div key={i} data-testid={`idea-${i}`} className="border border-neutral-200 rounded-xl p-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-neutral-900 text-white">
                    {TYPE_LABEL[idea.format] || idea.format}
                  </span>
                  <h4 className="font-head font-semibold text-neutral-900">{idea.title}</h4>
                </div>
                <p className="text-[15px] text-neutral-800 mt-2 font-medium">„{idea.hook}"</p>
                {idea.angle && <p className="text-sm text-neutral-500 mt-1">{idea.angle}</p>}
                <button
                  data-testid={`idea-create-${i}`}
                  onClick={() => createContent(idea)}
                  disabled={creating === idea.title}
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 transition disabled:opacity-50"
                >
                  {creating === idea.title ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                  Als Content anlegen
                </button>
              </div>
            ))}
            {!loading && ideas.length === 0 && <p className="text-sm text-neutral-400 py-6 text-center">Keine Ideen erhalten.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateTopicDialog({ open, setOpen, accountKey, onCreated }) {
  const [form, setForm] = useState({
    title: "", summary: "", heat: "warm", priority: "medium",
    reach_lever: "medium", event_relevant: false, sensitive: false, source: "",
  });
  const f = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Titel erforderlich");
    await api.post("/topics", { account_key: accountKey, ...form });
    toast.success("Thema angelegt");
    setOpen(false);
    setForm({ title: "", summary: "", heat: "warm", priority: "medium", reach_lever: "medium", event_relevant: false, sensitive: false, source: "" });
    onCreated();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="new-topic-btn" className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 transition-colors">
          <Plus className="h-4 w-4" /> Neues Thema
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-head">Neues Thema</DialogTitle>
          <DialogDescription>Lege ein neues Thema für den Themenradar an.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <DocScanButton kind="topic" onExtracted={(d) => setForm((s) => ({ ...s, ...d }))} />
          <Field label="Titel">
            <input data-testid="topic-title-input" value={form.title} onChange={(e) => f("title", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Zusammenfassung">
            <textarea rows={3} value={form.summary} onChange={(e) => f("summary", e.target.value)} className={`${inputCls} resize-none`} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Heat">
              <Select value={form.heat} onChange={(v) => f("heat", v)} options={[["hot", "Heiß"], ["warm", "Warm"], ["cold", "Kalt"]]} />
            </Field>
            <Field label="Priorität">
              <Select value={form.priority} onChange={(v) => f("priority", v)} options={[["high", "Hoch"], ["medium", "Mittel"], ["low", "Niedrig"]]} />
            </Field>
            <Field label="Reichweite">
              <Select value={form.reach_lever} onChange={(v) => f("reach_lever", v)} options={[["high", "Hoch"], ["medium", "Mittel"], ["low", "Niedrig"]]} />
            </Field>
          </div>
          <Field label="Quelle">
            <input value={form.source} onChange={(e) => f("source", e.target.value)} className={inputCls} />
          </Field>
          <div className="flex gap-5">
            <Check label="Eventrelevant" checked={form.event_relevant} onChange={(v) => f("event_relevant", v)} />
            <Check label="Sensibel" checked={form.sensitive} onChange={(v) => f("sensitive", v)} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="topic-save-btn" onClick={submit} className="bg-neutral-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-neutral-800">
            Thema speichern
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const inputCls = "w-full h-10 px-3 rounded-lg border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-medium text-neutral-600">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} bg-white`}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Check({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-sm text-neutral-700 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-neutral-300" />
      {label}
    </label>
  );
}
